import { z } from 'zod'

import { queryTerm, type SortOf, type SortRule, termField } from '@/components/directory/search'

import type { VehicleListParams } from './queries'

/** The sort values the API accepts: a field, or `-field` for descending. */
export const SORT_VALUES = [
  'created_at',
  '-created_at',
  'stock_number',
  '-stock_number',
  'year',
  '-year',
  'make',
  '-make',
  'updated_at',
  '-updated_at',
] as const satisfies readonly NonNullable<VehicleListParams['sort']>[]

/** Newest first, as the API defaults. Never written to the URL: the directory maps it to `undefined`. */
export const DEFAULT_SORT = '-created_at' satisfies VehicleSort

/** Next year's models reach the lot before the year does. */
export const MAX_MODEL_YEAR = new Date().getFullYear() + 1

/** A flag that is either on or absent: `false` never reaches the URL. */
const flag = z
  .boolean()
  .transform((value) => value || undefined)
  .optional()
  .catch(undefined)

/**
 * Route search params for the vehicle list: the search term, the filters and the sort. Every field
 * falls back to its default on its own, so one malformed value in a shared link drops that value
 * rather than the whole view.
 */
export const vehicleListSearchSchema = z
  .object({
    q: termField,
    status: z.enum(['available', 'out_of_service', 'sold']).optional().catch(undefined),
    make: z.string().trim().min(1).max(60).optional().catch(undefined),
    // A year typed into the address bar arrives as a string; the router only parses JSON it wrote.
    year: z.coerce.number().int().min(1900).max(MAX_MODEL_YEAR).optional().catch(undefined),
    loan: flag,
    disabled: flag,
    unlisted: flag,
    archived: flag,
    sort: z.enum(SORT_VALUES).optional().catch(undefined),
  })
  // A search string that is not an object at all (`?q[]=a&q[]=b`) falls back to the default view.
  .catch({})

export type VehicleListSearch = z.infer<typeof vehicleListSearchSchema>

export type VehicleSort = SortOf<VehicleListSearch>

/**
 * How the list sorts. Dates start descending, because newest first is what anyone sorting by
 * date wants; a stock number, a year or a make starts ascending.
 */
export const VEHICLE_SORT: SortRule<VehicleSort> = {
  default: DEFAULT_SORT,
  descendingFirst: ['created_at', 'updated_at'],
  words: {
    stock_number: 'stock number',
    make: 'vehicle',
    year: 'year',
    created_at: 'date added',
    updated_at: 'last change',
  },
}

/**
 * Search params -> the query the API takes. Defaults are left out, so the default view is a request
 * with no query string and a shared link asks for exactly what it shows.
 */
export function listParams(search: VehicleListSearch): VehicleListParams {
  const params: VehicleListParams = {}
  // Shorter queries are ignored by the API; not sending one keeps the unfiltered list on screen.
  const q = queryTerm(search)
  if (q) params.q = q
  if (search.status) params.status = search.status
  if (search.make) params.make = search.make
  if (search.year) params.year = search.year
  if (search.loan) params.is_loan_vehicle = true
  if (search.disabled) params.include_disabled = true
  // Archived cars are out of the list by default, so this one adds rather than narrows — the same
  // shape as `disabled`, and the reason `hasActiveFilters` still counts it: the view is not the
  // default view while it is on.
  if (search.archived) params.include_archived = true
  if (search.unlisted) params.listed = false
  if (search.sort && search.sort !== DEFAULT_SORT) params.sort = search.sort
  return params
}
